import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Sipariş onay kutusu motoru — sepet tarafı durum yönetimi.
 *
 * Backend, sepet cevabındaki her satıra `consent` alanını koyar. Kural katalog
 * kategorisi veya tekil ürün (SKU) seviyesinde tanımlanabilir; çakışırsa en
 * spesifik olan uygulanır (ürün > kategori). Bir satıra kural atanmamışsa
 * `consent` null gelir ve o satır için hiç kutu gösterilmez.
 *
 * Kurallar:
 *  - Kutu ön-işaretli GELMEZ; kullanıcı aktif olarak işaretler.
 *  - İşaretlenmeden sipariş ilerlemez.
 *  - Genel şartlar / mesafeli satış sözleşmesi onayından AYRI bir kutudur.
 *
 * @param {Array} cartItems Sepet satırları ({ id, consent }).
 * @returns {{
 *   isChecked: (cartProductId: number) => boolean,
 *   toggle: (cartProductId: number, value: boolean) => void,
 *   requiredItems: Array,
 *   missingCount: number,
 *   allAccepted: boolean,
 *   acknowledgements: Array
 * }}
 */
const useOrderConsents = (cartItems) => {
  const [checked, setChecked] = useState({});

  // Kutu gerektiren satırlar (backend tarafından çözümlenmiş hâliyle).
  const requiredItems = useMemo(
    () => (cartItems || []).filter((item) => item?.consent?.ruleId),
    [cartItems]
  );

  // Sepet değişince (ürün silme/adet değiştirme) artık var olmayan satırların
  // işaretini bırakma; metin sürümü güncellendiyse onayı düşür — kullanıcı yeni
  // metni görüp tekrar onaylamalı.
  useEffect(() => {
    setChecked((previous) => {
      const next = {};
      requiredItems.forEach((item) => {
        const previousState = previous[item.id];
        if (
          previousState &&
          previousState.ruleId === item.consent.ruleId &&
          previousState.textVersion === item.consent.textVersion
        ) {
          next[item.id] = previousState;
        }
      });
      return next;
    });
  }, [requiredItems]);

  const isChecked = useCallback(
    (cartProductId) => Boolean(checked[cartProductId]),
    [checked]
  );

  const toggle = useCallback(
    (cartProductId, value) => {
      const item = requiredItems.find(
        (requiredItem) => requiredItem.id === cartProductId
      );
      if (!item) return;

      setChecked((previous) => {
        const next = { ...previous };
        if (value) {
          next[cartProductId] = {
            ruleId: item.consent.ruleId,
            textVersion: item.consent.textVersion,
          };
        } else {
          delete next[cartProductId];
        }
        return next;
      });
    },
    [requiredItems]
  );

  const missingCount = requiredItems.filter((item) => !checked[item.id]).length;

  // Sipariş / ödeme isteğiyle birlikte gönderilecek onay listesi. Metin
  // gönderilmez; sunucu kuralı sepetten yeniden çözümleyip doğrular.
  const acknowledgements = useMemo(
    () =>
      Object.keys(checked).map((cartProductId) => ({
        cartProductId: Number(cartProductId),
        ruleId: checked[cartProductId].ruleId,
        textVersion: checked[cartProductId].textVersion,
      })),
    [checked]
  );

  return {
    isChecked,
    toggle,
    requiredItems,
    missingCount,
    allAccepted: missingCount === 0,
    acknowledgements,
  };
};

export default useOrderConsents;
